import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus,
  Building2,
  Briefcase,
  X,
  Search
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Assunzioni = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [cittadini, setCittadini] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [ruoli, setRuoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAzienda, setSelectedAzienda] = useState('');
  const [selectedRuolo, setSelectedRuolo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = isSindacatoAdmin();
  const canGestisci = () => isAdmin || hasPermesso('gestisci_dipendenti') || hasPermesso('gestisci_assunzioni');

  // I direttori possono assumere solo nella propria azienda
  const getAziendeDisponibili = () => {
    if (isAdmin) return aziende;
    // Per i direttori: solo la propria azienda
    return aziende.filter(a => a.id === user?.azienda_id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Se non admin, preseleziona automaticamente la propria azienda
    if (!isAdmin && user?.azienda_id) {
      setSelectedAzienda(user.azienda_id);
    }
  }, [isAdmin, user]);

  const fetchData = async () => {
    try {
      const [cittadiniRes, aziendeRes, ruoliRes] = await Promise.all([
        axios.get(`${API_URL}/cittadini`),
        axios.get(`${API_URL}/aziende`),
        axios.get(`${API_URL}/ruoli-azienda`)
      ]);
      setCittadini(cittadiniRes.data);
      setAziende(aziendeRes.data);
      setRuoli(ruoliRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleAssumi = async () => {
    if (!selectedUser || !selectedAzienda || !selectedRuolo) {
      toast.error('Seleziona tutti i campi');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/assunzioni?user_id=${selectedUser.id}&azienda_id=${selectedAzienda}&ruolo_azienda_id=${selectedRuolo}`);
      toast.success('Dipendente assunto!');
      setDialogOpen(false);
      setSelectedUser(null);
      setSelectedAzienda('');
      setSelectedRuolo('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleLicenzia = async (userId, aziendaId) => {
    if (!window.confirm('Confermi il licenziamento?')) return;
    
    try {
      await axios.delete(`${API_URL}/assunzioni/${userId}/${aziendaId}`);
      toast.success('Dipendente licenziato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openAssumiDialog = (utente) => {
    setSelectedUser(utente);
    // Se non admin, preseleziona la propria azienda
    if (!isSindacatoAdmin() && user?.azienda_id) {
      setSelectedAzienda(user.azienda_id);
    }
    setDialogOpen(true);
  };

  const getRuoliPerAzienda = (aziendaId) => {
    return ruoli.filter(r => r.azienda_id === aziendaId);
  };

  const filteredCittadini = cittadini.filter(c => 
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Aziende disponibili per l'utente corrente
  const aziendeDisponibili = isSindacatoAdmin() 
    ? aziende 
    : aziende.filter(a => a.id === user?.azienda_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!canGestisci()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Non hai i permessi per gestire le assunzioni</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assunzioni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
            <Users className="w-7 h-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Gestione Assunzioni</h1>
            <p className="text-slate-500">Assumi cittadini nella tua azienda</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Cerca cittadino per nome, cognome o username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista Cittadini */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredCittadini.map((c) => (
          <Card key={c.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{c.nome} {c.cognome}</CardTitle>
                  <p className="text-sm text-slate-500">@{c.username}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => openAssumiDialog(c)}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Assumi
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {c.impieghi && c.impieghi.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">Impieghi attuali:</p>
                  {c.impieghi.map((imp, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium">{imp.azienda_nome}</p>
                          <p className="text-xs text-slate-500">{imp.ruolo_nome || 'Nessun ruolo'}</p>
                        </div>
                      </div>
                      {(isSindacatoAdmin() || imp.azienda_id === user?.azienda_id) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleLicenzia(c.id, imp.azienda_id)}
                          className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-400">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm">Disoccupato</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCittadini.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun cittadino trovato</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog Assunzione */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assumi Dipendente</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedUser.nome} {selectedUser.cognome}</p>
                <p className="text-sm text-slate-500">@{selectedUser.username}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Azienda</Label>
                {isAdmin ? (
                  <Select value={selectedAzienda} onValueChange={(v) => { setSelectedAzienda(v); setSelectedRuolo(''); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona azienda..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getAziendeDisponibili().map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="bg-slate-100 p-2 rounded-lg text-sm">
                    {user?.azienda_nome || 'La tua azienda'}
                    <p className="text-xs text-slate-500 mt-1">Puoi assumere solo nella tua azienda</p>
                  </div>
                )}
              </div>

              {selectedAzienda && (
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select value={selectedRuolo} onValueChange={setSelectedRuolo}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona ruolo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getRuoliPerAzienda(selectedAzienda).map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleAssumi}
                  disabled={!selectedAzienda || !selectedRuolo}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assumi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assunzioni;
